/**
 * Canonical page ids BiQ receives so it can bias skills and answers toward
 * whatever screen the user is looking at. Keep this list in sync with
 * container_chat_app/backend/include/page_context.py ALLOWED_PAGES.
 */

const PAGE_CATALOG = [
  { test: (p) => p === '/map' || p === '/sea-tracking', id: 'map', label: 'Global Produce Map' },
  { test: (p) => p === '/analytics' || p === '/lookerstudio', id: 'analytics', label: 'Analytics' },
  { test: (p) => p.startsWith('/alerts'), id: 'alerts', label: 'Alerts' },
  { test: (p) => p === '/biq' || p === '/' || p === '/chatbot', id: 'biq', label: 'Speak with BiQ' },
  { test: (p) => p === '/produce-planner', id: 'procurement', label: 'Produce Planner' },
  { test: (p) => p === '/overview', id: 'overview', label: 'Overview' },
  { test: (p) => p === '/account', id: 'account', label: 'Account' },
  { test: (p) => p === '/billing' || p === '/membership', id: 'billing', label: 'Billing' },
  { test: (p) => p === '/usage', id: 'usage', label: 'Usage' },
  { test: (p) => p === '/privacy', id: 'privacy', label: 'Privacy' },
  { test: (p) => p === '/subscription', id: 'paywall', label: 'Upgrade' },
  { test: (p) => p === '/faq', id: 'faq', label: 'FAQ' },
  { test: (p) => p === '/contact', id: 'contact', label: 'Contact' },
];

const trimStr = (value, max = 80) => {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  return s.length > max ? s.slice(0, max) : s;
};


const toInt = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
};

/**
 * Build the payload sent as `page_context` on POST /chat.
 * `extras` is optional page-owned state (commodity, scorecards, dates); `hazard` is the
 * compact summary of the hazard selected on the map.
 */
export function buildBiqPageContext(pathname, search, extras, hazard) {
  const path = pathname || '/';
  const match = PAGE_CATALOG.find((entry) => entry.test(path));
  const page = match || { id: 'unknown', label: path };

  const params = typeof search === 'string'
    ? new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    : search instanceof URLSearchParams
      ? search
      : new URLSearchParams();

  const ctx = {
    page_id: page.id,
    page_label: page.label,
    path,
  };

  const urlCommodity = trimStr(params.get('commodity'));
  if (urlCommodity) ctx.commodity = urlCommodity;

  const urlLayer = trimStr(params.get('layer'));
  if (urlLayer) ctx.layer = urlLayer;

  const urlStatus = trimStr(params.get('status'), 200);
  if (urlStatus) ctx.status = urlStatus;

  const extra = extras && typeof extras === 'object' ? extras : null;
  if (extra) {
    if (!ctx.commodity && extra.commodity) ctx.commodity = trimStr(extra.commodity);
    if (!ctx.layer && extra.layer) ctx.layer = trimStr(extra.layer);
    if (extra.view) ctx.view = trimStr(extra.view);
    if (extra.date_start) ctx.date_start = trimStr(extra.date_start, 16);
    if (extra.date_end) ctx.date_end = trimStr(extra.date_end, 16);
    if (extra.panel) ctx.panel = trimStr(extra.panel);

    const arriving = extra.arriving;
    if (arriving && typeof arriving === 'object') {
      const out = {};
      for (const key of ['d3', 'd7', 'd10']) {
        const row = arriving[key];
        if (!row || typeof row !== 'object') continue;
        const shipments = toInt(row.shipments);
        const boxes = toInt(row.boxes);
        if (shipments == null && boxes == null) continue;
        out[key] = {
          ...(shipments != null ? { shipments } : {}),
          ...(boxes != null ? { boxes } : {}),
        };
      }
      if (Object.keys(out).length) ctx.arriving = out;
    }

    const weatherAlerts = toInt(extra.weather_alerts_active);
    if (weatherAlerts != null) ctx.weather_alerts_active = weatherAlerts;
    const harvestAlerts = toInt(extra.harvest_alerts);
    if (harvestAlerts != null) ctx.harvest_alerts = harvestAlerts;

    // Settings screen facts (Account / Usage / Upgrade) — compact only.
    if (typeof extra.whatsapp_linked === 'boolean') {
      ctx.whatsapp_linked = extra.whatsapp_linked;
    }
    if (extra.tier) ctx.tier = trimStr(extra.tier, 40);
    const resetInDays = toInt(extra.reset_in_days);
    if (resetInDays != null) ctx.reset_in_days = resetInDays;
    if (typeof extra.is_promotional === 'boolean') {
      ctx.is_promotional = extra.is_promotional;
    }
    if (extra.expires_at) ctx.expires_at = trimStr(extra.expires_at, 64);

    const features = extra.features;
    if (features && typeof features === 'object') {
      const out = {};
      for (const key of [
        'biq',
        'deep_thinking',
        'analytics',
        'smart_alerts',
        'custom_alerts',
      ]) {
        const row = features[key];
        if (!row || typeof row !== 'object') continue;
        const used = toInt(row.used);
        // limit may be -1 (unlimited); toInt rejects negatives.
        let limit = null;
        if (row.limit != null && Number.isFinite(Number(row.limit))) {
          const n = Math.round(Number(row.limit));
          if (n >= -1) limit = n;
        }
        // Keep display precision (do not round via toInt).
        let percentage = null;
        if (row.percentage != null && Number.isFinite(Number(row.percentage))) {
          const p = Number(row.percentage);
          percentage = Number.isInteger(p) ? p : Math.round(p * 100) / 100;
          if (percentage < 0) percentage = null;
        }
        const compact = {
          ...(used != null ? { used } : {}),
          ...(limit != null ? { limit } : {}),
          ...(percentage != null ? { percentage } : {}),
          ...(typeof row.is_unlimited === 'boolean'
            ? { is_unlimited: row.is_unlimited }
            : {}),
        };
        if (Object.keys(compact).length) out[key] = compact;
      }
      if (Object.keys(out).length) ctx.features = out;
    }
  }

  if (hazard && typeof hazard === 'object') {
    const h = {};
    if (hazard.name) h.name = trimStr(hazard.name, 60);
    if (hazard.type) h.type = trimStr(hazard.type, 20);
    if (hazard.class) h.class = trimStr(hazard.class, 40);
    if (hazard.location) h.location = trimStr(hazard.location, 60);
    if (hazard.updated) h.updated = trimStr(hazard.updated, 24);
    if (hazard.status === 'current' || hazard.status === 'ended') h.status = hazard.status;
    if (hazard.path) h.path = trimStr(hazard.path, 12);
    if (hazard.exposure) h.exposure = trimStr(hazard.exposure, 12);
    if (Object.keys(h).length) ctx.hazard = h;
  }

  return ctx;
}
